export const licenseViews = ["all", "expiring", "at_capacity", "revoked"] as const;
export type LicenseView = (typeof licenseViews)[number];
export type LicenseScope = {
  product_id?: string;
  customer_id?: string;
};
export type LicenseViewFilters = LicenseScope & {
  expires_from?: string;
  expires_before?: string;
  at_capacity?: boolean;
};

const scopeParams = ["product", "customer"] as const;

// Scope lives only in the URL (?product=, ?customer=) so it survives view and
// status changes.
export function licenseScope(search: URLSearchParams): LicenseScope {
  const product = search.get("product")?.trim();
  const customer = search.get("customer")?.trim();
  return { ...(product ? { product_id: product } : {}), ...(customer ? { customer_id: customer } : {}) };
}

export function withLicenseScope(params: URLSearchParams, source: URLSearchParams) {
  for (const param of scopeParams) {
    const value = source.get(param)?.trim();
    if (value) params.set(param, value);
  }
  return params;
}

export function licenseView(value: string | null): LicenseView {
  return licenseViews.includes(value as LicenseView) ? value as LicenseView : "all";
}

export function expiryWindow(now = new Date()) {
  return {
    expires_from: now.toISOString(),
    expires_before: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  };
}

const rfc3339Timestamp = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-](\d{2}):(\d{2}))$/;

function isRfc3339Timestamp(value: string) {
  const match = rfc3339Timestamp.exec(value);
  if (!match) return false;
  const [, year, month, day, hour, minute, second, , offsetHour = "0", offsetMinute = "0"] = match;
  if (
    Number(hour) > 23 ||
    Number(minute) > 59 ||
    Number(second) > 59 ||
    Number(offsetHour) > 23 ||
    Number(offsetMinute) > 59
  ) return false;
  const calendar = new Date(`${year}-${month}-${day}T00:00:00.000Z`);
  return Number.isFinite(Date.parse(value)) && calendar.getUTCFullYear() === Number(year) && calendar.getUTCMonth() === Number(month) - 1 && calendar.getUTCDate() === Number(day);
}

export function licenseViewFilters(search: URLSearchParams): LicenseViewFilters {
  const view = licenseView(search.get("view"));
  if (view === "at_capacity") return { at_capacity: true };
  if (view !== "expiring") return {};
  const from = search.get("expires_from");
  const before = search.get("expires_before");
  if (!from || !before || !isRfc3339Timestamp(from) || !isRfc3339Timestamp(before) || Date.parse(from) >= Date.parse(before)) return {};
  return { expires_from: from, expires_before: before };
}

export function licenseViewStatus(view: LicenseView, status: string) {
  if (view === "revoked") return "revoked";
  if (view === "expiring") return "active";
  return status;
}

export function canRetainLicenseView(view: LicenseView, status: string) {
  return licenseViewStatus(view, status) === status;
}
